import { EAutonomousCommunity } from '@constants';
import { IFile, IUser } from '@sdk/contracts';
import { Divider } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Wrapper,
  ItemWrapper,
  TitleWrapper,
  ButtonWrapper,
  ButtonTextWrapper
} from './StudentSpecificInfo.styles';

import { ExamGroupInfo } from '@/components/ExamGroup';
import { Icon } from '@/components/Icon';
import { useUserInfoByUser } from '@/store/users/user-info';

type StudentSpecificInfoProps = {
  student?: IUser;
};

export const StudentSpecificInfo: React.FC<StudentSpecificInfoProps> = ({ student }) => {
  const { t } = useTranslation(['DefaultTranslations', 'StudentProfileConfigurationTranslations']);
  const { userInfo, isPending } = useUserInfoByUser(student?._id);
  if (!student) return null;
  if (isPending || !userInfo) return null;
  const openFile = (file: IFile) => window.open(file.url, '_blank');

  const renderFile = (label: string, file?: IFile) => (
    <ItemWrapper>
      <TitleWrapper>{label}</TitleWrapper>
      <div>
        {file ? (
          <ButtonWrapper onClick={() => openFile(file)}>
            <Icon i="Attachment" />
            <ButtonTextWrapper>{file.name}</ButtonTextWrapper>
          </ButtonWrapper>
        ) : (
          '-'
        )}
      </div>
    </ItemWrapper>
  );

  const renderFileOptional = (label: string, file?: IFile) =>
    file ? renderFile(label, file) : null;

  const renderBoolean = (label: string, value?: boolean) => (
    <ItemWrapper>
      <TitleWrapper>{label}</TitleWrapper>
      <div>{value === undefined ? '-' : value ? t('YES') : t('NO')}</div>
    </ItemWrapper>
  );

  const renderBooleanOptional = (label: string, value: boolean | undefined) =>
    value === undefined ? null : renderBoolean(label, value);

  const renderText = (label: string, value?: string | number) => (
    <ItemWrapper>
      <TitleWrapper>{label}</TitleWrapper>
      <div>{value !== undefined && value !== '' ? String(value) : '-'}</div>
    </ItemWrapper>
  );

  const renderTextOptional = (label: string, value: string | number | undefined) =>
    value === undefined || value === '' ? null : renderText(label, value);

  return (
    <Wrapper>
      {userInfo.autonomousCommunity &&
        renderText(
          t('StudentProfileConfigurationTranslations:IN_WHICH_CA_DO_YOU_APPLY'),
          t(`DefaultTranslations:${userInfo.autonomousCommunity as EAutonomousCommunity}`)
        )}

      {/* Campos de estudio (UNI/PCE) */}
      {renderTextOptional(
        t('StudentProfileConfigurationTranslations:STUDY_BRANCH'),
        userInfo.studyBranch
      )}
      {renderTextOptional(
        t('StudentProfileConfigurationTranslations:WHAT_CAREER_DO_YOU_WANT_TO_STUDY'),
        userInfo.degree
      )}
      {renderTextOptional(
        t('StudentProfileConfigurationTranslations:UNIVERSITY'),
        userInfo.university
      )}
      {userInfo.university === 'other' &&
        renderTextOptional(
          t('StudentProfileConfigurationTranslations:UNIVERSITY'),
          userInfo.otherUniversity
        )}

      {renderBooleanOptional(
        t('StudentProfileConfigurationTranslations:HAS_MASTER_DEGREE'),
        userInfo.hasMasterDegree
      )}

      <Divider />

      {renderFileOptional(
        t('StudentProfileConfigurationTranslations:DNI_FILE'),
        userInfo.identityDocFile
      )}
      {renderFileOptional(
        t('StudentProfileConfigurationTranslations:SOCIAL_SECURITY_FILE'),
        userInfo.socialSecurityFile
      )}
      {renderFileOptional(
        t('StudentProfileConfigurationTranslations:DEGREE_FILE'),
        userInfo.degreeFile
      )}
      {renderFileOptional(
        t('StudentProfileConfigurationTranslations:NOTES_EQUIVALENCE_FILE'),
        userInfo.notesEquivalenceFile
      )}
      {renderFileOptional(
        t('StudentProfileConfigurationTranslations:SUBJECTS_TO_VALIDATE'),
        userInfo.subjectsToValidate
      )}

      {renderBooleanOptional(
        t('StudentProfileConfigurationTranslations:DO_YOU_NEED_STUDENT_VISA'),
        userInfo.needVisa
      )}
      {renderBooleanOptional(
        t('StudentProfileConfigurationTranslations:HAS_CERTIFICATE_DEGREE'),
        userInfo.hasCertificateDegree
      )}
      {renderBooleanOptional(
        t('StudentProfileConfigurationTranslations:HAS_NOTES_EQUIVALENCE'),
        userInfo.hasNotesEquivalence
      )}

      {userInfo.examGroups && userInfo.examGroups.length > 0 && (
        <ItemWrapper>
          <TitleWrapper>
            {t('StudentProfileConfigurationTranslations:EXAM_GROUP_DATE')}
          </TitleWrapper>
          <ExamGroupInfo examGroupsIds={userInfo.examGroups} />
        </ItemWrapper>
      )}
    </Wrapper>
  );
};
